import { useEffect, useState } from 'react';
import { Check, Hash, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import { useCustomerGroup } from '@/hooks/admin/useCustomerGroup';
import { useSetStoreCode } from '@/hooks/admin/useCustomerContacts';

const inputCls =
  'rounded-lg border border-brix-border bg-brix-surface px-2.5 py-1.5 text-xs text-brix-ink focus:outline-none focus:ring-1 focus:ring-brix-primary';

/**
 * The chain's own store number for this account.
 *
 * Only shown for a member store of a chain/franchise group — a standalone
 * customer has no chain to be numbered within. This is what an emailed
 * purchase order routes on: a PO numbered "055-00183" carries the store
 * number in its prefix and matches this field exactly, before any name or
 * city guessing gets a chance to be wrong.
 */
export function StoreCodeCard({
  customerId,
  storeCode,
  onSaved,
}: {
  customerId: string;
  storeCode: string | null;
  onSaved: () => void;
}) {
  const { data: group } = useCustomerGroup(customerId);
  const save = useSetStoreCode(customerId);
  const [draft, setDraft] = useState(storeCode ?? '');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(storeCode ?? '');
  }, [storeCode]);

  // Members of a group only. `member_of` is set regardless of billing mode.
  if (!group?.member_of) return null;

  const dirty = draft.trim() !== (storeCode ?? '');

  const commit = async () => {
    const value = draft.trim() === '' ? null : draft.trim();
    await save.mutateAsync(value);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    onSaved();
  };

  return (
    <CollapsibleCard
      title="Store code"
      icon={<Hash size={14} className="text-brix-muted shrink-0" />}
      storageKey={`store-code-${customerId}`}
      defaultOpen={false}
      tone={storeCode ? 'default' : 'warn'}
      summary={storeCode ? `#${storeCode}` : 'Not set'}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. 0055"
          className={`${inputCls} w-28 font-mono`}
          aria-label="Chain store code"
        />
        <Button size="sm" onClick={() => void commit()} disabled={!dirty || save.isPending}>
          {save.isPending ? <Loader2 size={13} className="animate-spin" /> : 'Save'}
        </Button>
        {saved ? (
          <span className="text-[11px] text-emerald-600 flex items-center gap-1">
            <Check size={12} /> Saved
          </span>
        ) : null}
      </div>
      <p className="text-[12px] text-brix-muted">
        {group.member_of.master_name}&rsquo;s own number for this store. Purchase orders emailed in
        are routed on this — a PO numbered <span className="font-mono">055-00183</span> lands on
        store <span className="font-mono">0055</span>. Leave blank if this store has no number.
      </p>
      {save.error ? (
        <p className="text-[12px] text-red-500">
          {save.error instanceof Error ? save.error.message : 'Could not save the store code.'}
        </p>
      ) : null}
    </CollapsibleCard>
  );
}

export default StoreCodeCard;
